<template>
  <i-modal
    :title="$t(isEdit ? 'editing' : 'add', { value: $tc('apparel_style', 1) })"
    :value="value"
    :load="loading"
    @submit="save"
    @input="$emit('input', $event)"
  >
    <div class="modal-body">
      <v-row class="my-0">
        <v-col cols="12">
          <v-select
            v-model="form.apparel_id"
            :items="apparels"
            :item-text="getApparelLabel"
            item-value="id"
            :label="$tc('apparel_name', 1)"
            :loading="loadingApparels"
            :disabled="loading || loadingApparels"
            :rules="[(v) => !!v || $t('required_field')]"
            outlined
            color="secondary"
          />
        </v-col>
      </v-row>
      <v-row class="my-0">
        <v-col cols="12">
          <ValidationProvider
            vid="name"
            :name="$tc('name', 1)"
            rules="required|min:2|max:128"
            v-slot="{ errors }"
          >
            <v-text-field
              autofocus
              name="name"
              :error-messages="errors[0]"
              v-model="form.name"
              outlined
              color="secondary"
              :label="$tc('name', 1)"
              :loading="loading"
              @keyup.enter="save"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
    </div>
  </i-modal>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ApparelStyleModal',
  props: {
    /**
     * Controla la visibilidad del diálogo
     */
    value: {
      type: Boolean,
      required: true,
      default: false
    },
    /**
     * Objeto con los datos del estilo a editar (null para crear)
     */
    toEdit: {
      type: Object,
      default: null
    },
    defaultApparelId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      form: {
        name: '',
        apparel_id: null
      },
      apparels: [],
      loading: false,
      loadingApparels: false,
      isEdit: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getCompanyData',
      me: 'session/me'
    }),
    isSuperuser() {
      return !!(this.me && this.me.is_superuser)
    },
    /**
     * Obtiene el PK de la empresa de forma segura
     */
    companyPk() {
      return this.company ? this.company.pk || this.company.id : null
    }
  },
  watch: {
    /**
     * Cuando se recibe un elemento para editar, cargar los datos
     */
    toEdit: {
      handler(val) {
        if (val) {
          this.isEdit = true
          this.form.name = val.name || ''
        } else {
          this.isEdit = false
          this.form.name = ''
        }
      },
      immediate: true
    },
    /**
     * Cuando se abre el diálogo, preparar el formulario actual
     */
    value(val) {
      if (val) {
        this.setForm(this.toEdit)
        this.loadApparels()
      } else {
        this.form.name = ''
        this.form.apparel_id = null
        this.isEdit = false
      }
    }
  },
  methods: {
    getApparelLabel(item) {
      if (!item || !item.name) return ''
      const normalizedName = String(item.name)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      const translationPath = `apparel_names_translations.${normalizedName}`
      return this.$te(translationPath) ? this.$t(translationPath) : item.name
    },
    setForm(item) {
      this.isEdit = !!item
      this.form.name = item ? item.name || '' : ''
      this.form.apparel_id = item
        ? item.apparel_id || item.apparel?.id || item.apparel?.pk || null
        : this.defaultApparelId
    },
    async loadApparels() {
      if (!this.companyPk && !this.isSuperuser) {
        this.apparels = []
        return
      }
      this.loadingApparels = true
      try {
        const response = await this.$api.product.apparelName.list({
          opt: {
            params: {
              ...(this.isSuperuser
                ? { global: true }
                : { company_id: this.companyPk }),
              no_page: true,
              size: 1000
            }
          }
        })
        const items = response.data.results || response.data || []
        this.apparels = items.map((item) => ({
          ...item,
          id: item.id !== undefined ? item.id : item.pk
        }))
      } catch (error) {
        this.apparels = []
        this.$toast.error(this.$t('error_loading_apparel'))
        console.error('Error loading apparel for style:', error)
      } finally {
        this.loadingApparels = false
      }
    },
    /**
     * Guardar el estilo (crear o actualizar)
     */
    async save() {
      if (this.loading) return

      // Validar que la empresa esté cargada
      if (!this.companyPk && !this.isSuperuser) {
        this.$toast.error(
          'Error: No se pudo cargar la información de la empresa'
        )
        return
      }

      if (!this.form.name || this.form.name.trim() === '') {
        this.$toast.error(this.$t('this_field_is_required'))
        return
      }

      if (this.form.name.length < 2) {
        this.$toast.error(this.$t('min_length', { count: 2 }))
        return
      }

      if (this.form.name.length > 128) {
        this.$toast.error(this.$t('max_length', { count: 128 }))
        return
      }
      if (!this.form.apparel_id) {
        this.$toast.error(this.$t('required_field'))
        return
      }

      this.loading = true
      try {
        const payload = {
          name: this.form.name.trim(),
          apparel_id: this.form.apparel_id
        }
        if (this.companyPk) payload.company_id = this.companyPk

        let response
        if (this.isEdit) {
          response = await this.$api.product.apparelStyle.edit({
            pk: this.toEdit.id || this.toEdit.pk,
            form: payload
          })
          this.$toast.success(this.$t('updated_successfully'))
        } else {
          response = await this.$api.product.apparelStyle.create({
            form: payload
          })
          this.$toast.success(this.$t('created_successfully'))
        }

        this.$emit('done', response.data)
        this.$emit('input', false)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Manejar errores de la API
     * @param {Object} error - Error de la petición
     */
    handleError(error) {
      // Error de duplicado (nombre ya existe)
      if (error.response?.status === 409) {
        this.$toast.error(this.$t('style_name_already_exists'))
        return
      }

      // Error de integridad referencial (no se puede eliminar)
      if (error.response?.status === 400) {
        const message = error.response.data?.message || ''
        if (message.includes('integrit') || message.includes('foreign key')) {
          this.$toast.error(this.$t('cannot_delete_style_in_use'))
          return
        }
      }

      // Error de validación del backend
      if (error.response?.data) {
        const backendErrors = error.response.data.errors || error.response.data
        if (backendErrors.name) {
          this.$toast.error(backendErrors.name[0])
          return
        }
        if (backendErrors.apparel_id || backendErrors.apparel) {
          const errors = backendErrors.apparel_id || backendErrors.apparel
          this.$toast.error(Array.isArray(errors) ? errors[0] : errors)
          return
        }
        if (backendErrors.company) {
          this.$toast.error(backendErrors.company[0])
          return
        }
      }

      // Error de red o genérico
      if (error.message?.includes('Network Error')) {
        this.$toast.error(this.$t('network_error'))
      } else {
        this.$toast.error(this.$t('error_saving_style'))
      }

      console.error('Error saving style:', error)
    }
  }
}
</script>

<style scoped></style>
